import { scroller, Link } from "react-scroll";
import { OctagonAlert } from 'lucide-react';
import { getMainTranslation } from '../../../hooks/useLanguage';
import {
  normalizeValidationErrorFieldId,
  extractFieldName,
  extractItemNumber,
  extractDocPkgNumber
} from '../../../utils/fieldIdUtils';

export const ValidationErrors = ({ validationErrors, isSpanish, splitView }) => {
  
  const handleErrorFieldClick = (e, field) => {
    e.preventDefault();

    // Normalize the field ID to match input element format
    const normalizedField = normalizeValidationErrorFieldId(field);
    const element = document.getElementById(normalizedField);
    if (element) {
      // In split view, use scrollable container instead of react-scroll
      if (splitView) {
        const scrollableContainer = element.closest('.split-declaration-height') || element.closest('.overflow-auto');
        if (scrollableContainer) {
          // Calculate position relative to container and scroll to center
          const containerRect = scrollableContainer.getBoundingClientRect();
          const elementRect = element.getBoundingClientRect();
          const scrollTop = scrollableContainer.scrollTop;
          const elementTop = elementRect.top - containerRect.top + scrollTop;
          const scrollPosition = elementTop - (containerRect.height / 2) + (elementRect.height / 2);
          
          scrollableContainer.scrollTo({
            top: Math.max(0, scrollPosition),
            behavior: 'smooth'
          });
        } else {
          // Fallback to regular scrollIntoView
          element.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
            inline: 'nearest'
          });
        }
      } else {
        // In normal view, use react-scroll with the analysis-report container
        try {
      scroller.scrollTo(normalizedField, {
        duration: 100,
        offset: -300,
        smooth: "easeInOutQuint",
        containerId: "analysis-report",
      });
        } catch (error) {
          // Fallback if react-scroll fails
          console.warn('react-scroll failed, using scrollIntoView:', error);
          element.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
            inline: 'nearest'
          });
        }
      }

      setTimeout(() => {
        element.classList.add("highlight-error");

        setTimeout(() => {
          element.classList.remove("highlight-error");
        }, 5000);
      }, 300);
    }
  };

  // CRITICAL: Check if validationErrors exist and have actual errors validationErrors is an array of error objects
  const hasErrors = validationErrors && Array.isArray(validationErrors) && validationErrors.length > 0;

  return (
    <>
        <div className={`${splitView ? 'p-4 bg-gray-50 border-red-200 rounded-lg border-container-force' : ''} h-full`}>
          <h2 className="font-semibold text text-red-600 ml-4">
            {!isSpanish ? "Required Fields Missing" : "Campos Requeridos Faltantes"}
          </h2>
          {hasErrors && (
            <span className="flex text-gray-500 text-xs pl-4">{getMainTranslation('fixAllRequired', isSpanish)}</span>
          )} 
        {hasErrors ? (
          <div className="text-sm text-red-500 space-y-4 mt-6">
            {validationErrors?.map((declaration, index) => {
              // Organize errors by item
              const itemsMap = new Map();
              
              // Process item errors
              declaration.itemErrors?.forEach((itemError) => {
                const itemNum = extractItemNumber(itemError.title);
                if (itemNum !== null) {
                  if (!itemsMap.has(itemNum)) {
                    itemsMap.set(itemNum, {
                      itemIndex: itemNum,
                      itemErrors: [],
                      documents: new Map(),
                      packages: new Map()
                    });
                  }
                  itemsMap.get(itemNum).itemErrors = itemError.errors || [];
                }
              });

              // Process document errors
              declaration.documentErrors?.forEach((docError) => {
                const itemNum = extractItemNumber(docError.itemTitle);
                const docNum = extractDocPkgNumber(docError.documentTitle);
                if (itemNum !== null && docNum !== null) {
                  if (!itemsMap.has(itemNum)) {
                    itemsMap.set(itemNum, {
                      itemIndex: itemNum,
                      itemErrors: [],
                      documents: new Map(),
                      packages: new Map()
                    });
                  }
                  const item = itemsMap.get(itemNum);
                  if (!item.documents.has(docNum)) {
                    item.documents.set(docNum, []);
                  }
                  item.documents.get(docNum).push(...(docError.errors || []));
                }
              });

              // Process package errors
              declaration.packageErrors?.forEach((pkgError) => {
                const itemNum = extractItemNumber(pkgError.itemTitle);
                const pkgNum = extractDocPkgNumber(pkgError.packageTitle);
                if (itemNum !== null && pkgNum !== null) {
                  if (!itemsMap.has(itemNum)) {
                    itemsMap.set(itemNum, {
                      itemIndex: itemNum,
                      itemErrors: [],
                      documents: new Map(),
                      packages: new Map()
                    });
                  }
                  const item = itemsMap.get(itemNum);
                  if (!item.packages.has(pkgNum)) {
                    item.packages.set(pkgNum, []);
                  }
                  item.packages.get(pkgNum).push(...(pkgError.errors || []));
                }
              });

              // Convert maps to sorted arrays
              const organizedItems = Array.from(itemsMap.values())
                .sort((a, b) => a.itemIndex - b.itemIndex)
                .map(item => ({
                  ...item,
                  documents: Array.from(item.documents.entries())
                    .sort(([a], [b]) => a - b)
                    .map(([docIndex, errors]) => ({ docIndex, errors })),
                  packages: Array.from(item.packages.entries())
                    .sort(([a], [b]) => a - b)
                    .map(([pkgIndex, errors]) => ({ pkgIndex, errors }))
                }));

              const hasDeclarationErrors = declaration?.declarationErrors?.length > 0;
              const hasItems = organizedItems.length > 0;

              if (!hasDeclarationErrors && !hasItems) {
                return null;
              }

              return (
                <div key={index} className="mb-4">
                  <h3 className="font-semibold text-base mb-2 text-gray-700 ml-4">
                    {!isSpanish ? "Export" : "Expediente"} #{declaration.declarationNumber}
                  </h3>

                  {/* Declaration Errors */}
                  {hasDeclarationErrors && (
                    <div className="space-y-2 mb-3">
                      {declaration.declarationErrors.map((error, i) => {
                        const actualFieldName = extractFieldName(error.field);
                        const normalizedField = normalizeValidationErrorFieldId(error.field);
                        return (
                          <div
                            key={i}
                            id={`error_${normalizedField}`}
                            className="p-3 px-4 rounded-full text-sm break-words bg-red-50 cursor-pointer hover:opacity-90 transition-opacity"
                            onClick={(e) => handleErrorFieldClick(e, error.field)}
                          >
                            <div className="flex-1 min-w-0 flex gap-2 truncate rounded-full">
                              <Link
                                to={normalizedField}
                                onClick={(e) => handleErrorFieldClick(e, error.field)}
                                className="font-semibold break-words text-gray-700"
                              >
                                {error.error}
                              </Link>
                              <div className="text-[10px] text-gray-500 font-mono break-all truncate bottom-[-2px] relative">
                                {actualFieldName}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {/* Items */}
                  {organizedItems.map((item, itemIdx) => {
                    const hasItemErrors = item.itemErrors.length > 0;
                    const hasDocuments = item.documents.length > 0;
                    const hasPackages = item.packages.length > 0;
                    const hasItemData = hasItemErrors || hasDocuments || hasPackages;

                    if (!hasItemData) return null;

                    return (
                      <div key={itemIdx} className="ml-4 mb-3">
                        <h4 className="font-medium text-sm mb-2 text-gray-600 ml-4">
                          {!isSpanish ? "Item" : "Partida"} #{item.itemIndex + 1}
                        </h4>

                        {/* Item Errors */}
                        {hasItemErrors && (
                          <div className="space-y-2 mb-2">
                            {item.itemErrors.map((error, errIdx) => {
                              const actualFieldName = extractFieldName(error.field);
                              const normalizedField = normalizeValidationErrorFieldId(error.field);
                              return (
                                <div
                                  key={errIdx}
                                  id={`error_${normalizedField}`}
                                  className="p-3 px-4 rounded-full text-sm break-words bg-red-50 cursor-pointer hover:opacity-90 transition-opacity"
                                  onClick={(e) => handleErrorFieldClick(e, error.field)}
                                >
                                  <div className="flex-1 min-w-0 flex gap-2 truncate rounded-full">
                                    <Link
                                      to={normalizedField}
                                      onClick={(e) => handleErrorFieldClick(e, error.field)}
                                      className="font-semibold break-words text-gray-700"
                                    >
                                      {error.error}
                                    </Link>
                                    <div className="text-[10px] text-gray-500 font-mono break-all truncate bottom-[-2px] relative">
                                      {actualFieldName}
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}

                        {/* Documents */}
                        {hasDocuments && (
                          <div className="ml-4 mb-2">
                            <h5 className="font-medium text-xs mb-1 text-gray-500">
                              {!isSpanish ? "Documents" : "Documentos"}
                            </h5>
                            {item.documents.map((doc, docIdx) => (
                              <div key={docIdx} className="ml-4 mb-2">
                                <p className="text-xs font-medium mb-1 text-gray-500">
                                  {!isSpanish ? "Document" : "Documento"} #{doc.docIndex + 1}
                                </p>
                                <div className="space-y-2">
                                  {doc.errors.map((error, errIdx) => {
                                    const actualFieldName = extractFieldName(error.field);
                                    const normalizedField = normalizeValidationErrorFieldId(error.field);
                                    return (
                                      <div
                                        key={errIdx}
                                        id={`error_${normalizedField}`}
                                        className="p-3 px-4 rounded-full text-sm break-words bg-red-50 cursor-pointer hover:opacity-90 transition-opacity"
                                        onClick={(e) => handleErrorFieldClick(e, error.field)}
                                      >
                                        <div className="flex-1 min-w-0 flex gap-2 truncate">
                                          <Link
                                            to={normalizedField}
                                            onClick={(e) => handleErrorFieldClick(e, error.field)}
                                            className="font-semibold break-words text-gray-700"
                                          >
                                            {error.error}
                                          </Link>
                                          <div className="text-[10px] text-gray-500 font-mono break-all truncate bottom-[-2px] relative">
                                            {actualFieldName}
                                          </div>
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}

                        {/* Packages */}
                        {hasPackages && (
                          <div className="ml-4 mb-2">
                            <h5 className="font-medium text-xs mb-1 text-gray-500">
                              {!isSpanish ? "Packages" : "Embalajes"}
                            </h5>
                            {item.packages.map((pkg, pkgIdx) => (
                              <div key={pkgIdx} className="ml-4 mb-2">
                                <p className="text-xs font-medium mb-1 text-gray-500">
                                  {!isSpanish ? "Package" : "Embalaje"} #{pkg.pkgIndex + 1}
                                </p>
                                <div className="space-y-2">
                                  {pkg.errors.map((error, errIdx) => {
                                    const actualFieldName = extractFieldName(error.field);
                                    const normalizedField = normalizeValidationErrorFieldId(error.field);
                                    return (
                                      <div
                                        key={errIdx}
                                        id={`error_${normalizedField}`}
                                        className="p-3 px-4 rounded-full text-sm break-words bg-red-50 cursor-pointer hover:opacity-90 transition-opacity"
                                        onClick={(e) => handleErrorFieldClick(e, error.field)}

                                      >
                                        <div className="flex-1 min-w-0 flex gap-2 truncate">
                                          <Link
                                            to={normalizedField}
                                            onClick={(e) => handleErrorFieldClick(e, error.field)}
                                            className="font-semibold break-words text-gray-700"
                                          >
                                            {error.error}
                                          </Link>
                                          <div className="text-[10px] text-gray-500 font-mono break-all truncate bottom-[-2px] relative">
                                            {actualFieldName}
                                          </div>
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-xs text-gray-500 mb-2 ml-4">
            {!isSpanish ? "No required inputs missing" : "No faltan entradas requeridas"}
        </div>
      )}
      </div>
    </>
  );
};

